/**
 * Helper to find user by email
 * @param {string} email - Email to search for
 * @param {Array} users - Array of user objects
 * @returns {Object|null} - User object if found, null otherwise
 */
const findUserByEmail = (email, users) => {
  if (!email || !users) return null;
  const emailLower = email.toLowerCase();
  return users.find((user) => user.email?.toLowerCase() === emailLower);
};

/**
 * Core helper to classify a single item as user or email
 * @param {Object|string} item - User object or email string
 * @param {Array} users - Array of all users (to check if email matches existing user)
 * @returns {Object} - { isEmail: boolean, email: string, userId: number }
 */
const classifyItem = (item, users = []) => {
  if (!item) return { isEmail: false, email: null, userId: null };

  // Check if it's a string or email object
  let emailToCheck = null;
  if (typeof item === 'string') {
    emailToCheck = item;
  } else if (item._isNewEmail) {
    emailToCheck = item.email;
  }

  // If we have an email, check if it belongs to an existing user
  if (emailToCheck) {
    const existingUser = findUserByEmail(emailToCheck, users);
    if (existingUser) {
      // Email belongs to existing user - return as user, not email
      return { isEmail: false, email: null, userId: existingUser.id };
    }
    // It's a new invitation email
    return { isEmail: true, email: emailToCheck, userId: null };
  }

  // It's a user object
  if (item.id) {
    return { isEmail: false, email: null, userId: item.id };
  }

  return { isEmail: false, email: null, userId: null };
};

/**
 * Helper function to separate user IDs from emails in selection (multi-select)
 * @param {Array} items - Array of user objects and/or email objects
 * @param {Array} users - Array of all users (to check if email matches existing user)
 * @returns {Object} - { userIds: [1, 2, 3], emails: ["email@test.com"] }
 */
const separateUsersAndEmails = (items, users = []) => {
  const userIds = [];
  const emails = [];

  items.forEach((item) => {
    const { isEmail, email, userId } = classifyItem(item, users);
    if (isEmail) {
      emails.push(email);
    } else if (userId) {
      userIds.push(userId); // Store plain ID, treeUtils will wrap it
    }
  });

  return { userIds, emails };
};

/**
 * Helper function to get which roles an email is already in
 * @param {string} email - Email to check
 * @param {Object} data - Current form data
 * @param {string} currentRole - Current role being assigned
 * @returns {Array} - Array of role names the email is in (empty if not found)
 */
const getEmailExistingRoles = (email, data, currentRole) => {
  const emailLower = email.toLowerCase();
  const roles = [];

  // Helper to normalize invited field to array
  const toArray = (field) =>
    Array.isArray(field) ? field : field ? [field] : [];

  if (currentRole !== 'head') {
    const invitedHeads = toArray(data.invitedHead);
    if (invitedHeads.some((e) => e.toLowerCase() === emailLower)) {
      roles.push('Head');
    }
  }

  if (currentRole !== 'manager') {
    const invitedManagers = toArray(data.invitedManager);
    if (invitedManagers.some((e) => e.toLowerCase() === emailLower)) {
      roles.push('Manager');
    }
  }

  if (currentRole !== 'employee') {
    const invitedEmployees = data.invitedEmployees || [];
    if (invitedEmployees.some((e) => e.toLowerCase() === emailLower)) {
      roles.push('Employee');
    }
  }

  return roles;
};

/**
 * Helper function to check and set error for blocked emails
 * @param {Array} emails - Array of emails to check
 * @param {Object} data - Current form data
 * @param {string} currentRole - Current role being assigned
 * @param {Object} errors - Current errors
 * @param {Function} setErrors - Error setter function
 * @param {string} fieldName - Field name for error ('head', 'manager', 'employees')
 */
const checkAndSetEmailError = (
  emails,
  data,
  currentRole,
  errors,
  setErrors,
  fieldName
) => {
  const blockedEmails = emails
    .map((email) => ({
      email,
      roles: getEmailExistingRoles(email, data, currentRole),
    }))
    .filter(({ roles }) => roles.length > 0);

  if (blockedEmails.length > 0) {
    const errorMessages = blockedEmails.map(
      ({ email, roles }) => `${email} (already in ${roles.join(', ')})`
    );
    setErrors({
      ...errors,
      [fieldName]: `Cannot add: ${errorMessages.join(', ')}`,
    });
  }
};

/**
 * Creates event handler for head changes in Level 1 nodes (multi-select)
 * @param {Function} setSelectedHeads - State setter for local UI display
 * @param {Function} setData - State setter for form data
 * @param {Object} data - Current form data: { name: 'Engineering', description: 'Tech team', manager: [1,2], head: [3,4], employees: [5,6,7] }
 * @param {Function} clearFieldError - Utility to clear validation errors
 * @param {Object} errors - Current validation errors: { name: '', manager: '', head: '' }
 * @param {Function} setErrors - State setter for validation errors
 * @param {Function} setGlobalSelectedHeads - State setter for global head tracking
 * @param {Object} node - Current node: { id: 'node1', level: 1, name: 'Engineering', manager: [1,2], head: [3,4], employees: [5,6,7] }
 * @returns {Function} - Event handler function
 *
 * Logic: When heads are selected:
 * 1. Updates local selectedHeads state for UI
 * 2. Updates form data with formatted head data (existing users or new invitations)
 * 3. Clears validation errors for head field
 * 4. Updates global state to track heads across all nodes
 *
 * Example: User selects [head1, "newemail@example.com"] → formats as [{ type: 'existing', user_id: 5 }, { type: 'new', email: 'newemail@example.com' }]
 */
export const createHeadChangeHandler = (
  setSelectedHeads,
  setData,
  data,
  clearFieldError,
  errors,
  setErrors,
  setGlobalSelectedHeads,
  node,
  users
) => {
  return (e) => {
    const newHeadUsers = e.value || [];
    // Separate user IDs from emails (checks if email belongs to existing user)
    const { userIds, emails } = separateUsersAndEmails(newHeadUsers, users);

    // Filter out emails already used in other roles
    const filteredEmails = emails.filter(
      (email) => getEmailExistingRoles(email, data, 'head').length === 0
    );

    // Check and set error for blocked emails
    checkAndSetEmailError(emails, data, 'head', errors, setErrors, 'head');

    console.log('Heads selected:', newHeadUsers);

    setSelectedHeads(newHeadUsers);
    setData({
      ...data,
      head: userIds, // Store as [1, 2, 3] - plain IDs
      invitedHead: filteredEmails, // Store as ["email@test.com"] - filtered
    });
    clearFieldError(errors, setErrors, 'head');

    const newHeadEntry = {
      node_id: node.id,
      heads: userIds,
    };
    setGlobalSelectedHeads((prev) => {
      const filtered = prev.filter((item) => item.node_id !== node.id);
      return [...filtered, newHeadEntry];
    });
  };
};

/**
 * Creates event handler for single head changes (Level 1 and Level 2 nodes)
 * @param {Function} setSelectedSingleHead - State setter for local UI display
 * @param {Function} setData - State setter for form data
 * @param {Object} data - Current form data: { name: 'Frontend', description: 'UI team', manager: 3, head: 4, employees: [5,6] }
 * @param {Function} setErrors - State setter for validation errors
 * @param {Object} errors - Current validation errors
 * @param {Array} users - All users array
 * @param {Function} setGlobalSelectedHeads - State setter for global head tracking (optional, for Level 1)
 * @param {Object} node - Current node (optional, for Level 1)
 * @returns {Function} - Event handler function
 *
 * Logic: When a single head is selected:
 * 1. Updates local selectedSingleHead state for UI
 * 2. Updates form data with ID or email (backend will distinguish)
 * 3. For Level 1: Updates global state to exclude head from other dropdowns
 * 4. For Level 2: No global state update (heads come from parent)
 *
 * Example: User selects head1 → stores ID (4) or email ('test@email.com')
 */
export const createSingleHeadChangeHandler = (
  setSelectedSingleHead,
  setData,
  data,
  setErrors,
  errors,
  users,
  setGlobalSelectedHeads = null,
  node = null
) => {
  return (e) => {
    const newHeadUser = e.value;

    console.log('Single head selected:', newHeadUser);

    const { isEmail, email, userId } = classifyItem(newHeadUser, users);

    // Filter out email if already used in other roles
    const isEmailAllowed =
      isEmail && getEmailExistingRoles(email, data, 'head').length === 0;
    const filteredEmail = isEmailAllowed ? email : null;

    // Check and set error for blocked email
    if (isEmail) {
      checkAndSetEmailError([email], data, 'head', errors, setErrors, 'head');
    } else {
      // clear any previous head validation error when valid selection made
      if (errors.head) {
        setErrors({
          ...errors,
          head: '',
        });
      }
    }

    setSelectedSingleHead(newHeadUser);
    setData({
      ...data,
      head: userId || null, // Store plain ID, treeUtils will wrap it
      invitedHead: filteredEmail,
    });

    // for level 1, update global state to exclude head from other dropdowns
    if (setGlobalSelectedHeads && node && node.level === 1) {
      setGlobalSelectedHeads((prev) => {
        const filtered = prev.filter((item) => item.node_id !== node.id);
        return [
          ...filtered,
          {
            node_id: node.id,
            heads: userId ? [userId] : [], // wrap single ID in array for consistency with search utils
          },
        ];
      });
    }
  };
};

/**
 * Creates event handler for manager changes (different logic per level)
 * @param {Object} node - Current node: { id: 'node1', level: 1, name: 'Engineering', manager: [1,2], head: [3,4], employees: [5,6,7] }
 * @param {Function} setSelectedManager - State setter for local UI display
 * @param {Function} setData - State setter for form data
 * @param {Object} data - Current form data: { name: 'Engineering', description: 'Tech team', manager: [1,2] or 3, head: [4,5] or 6, employees: [7,8,9] }
 * @param {Function} clearFieldError - Utility to clear validation errors
 * @param {Object} errors - Current validation errors: { name: '', manager: '', head: '' }
 * @param {Function} setErrors - State setter for validation errors
 * @param {Function} setGlobalSelectedManagers - State setter for global manager tracking
 * @returns {Function} - Event handler function
 *
 * Logic:
 * - Level 1: Multi-select managers, NO global tracking (optional role)
 * - Level 2+: Single-select manager, WITH global tracking (important role)
 * Updates local state, form data with formatted data (existing users or new invitations), clears errors
 *
 * Example Level 1: [manager1, "email@example.com"] → formats as [{ type: 'existing', user_id: 7 }, { type: 'new', email: 'email@example.com' }]
 * Example Level 2+: manager1 → formats as { type: 'existing', user_id: 7 }
 */
export const createManagerChangeHandler = (
  node,
  setSelectedManager,
  setData,
  data,
  clearFieldError,
  errors,
  setErrors,
  setGlobalSelectedManagers,
  users
) => {
  return (e) => {
    if (node.level === 1) {
      // Multi-select for level 1 nodes
      const newManagerUsers = e.value || [];
      // Separate user IDs from emails (checks if email belongs to existing user)
      const { userIds, emails } = separateUsersAndEmails(
        newManagerUsers,
        users
      );

      // Filter out emails already used in other roles
      const filteredEmails = emails.filter(
        (email) => getEmailExistingRoles(email, data, 'manager').length === 0
      );

      // Check and set error for blocked emails
      checkAndSetEmailError(
        emails,
        data,
        'manager',
        errors,
        setErrors,
        'manager'
      );

      console.log('Managers selected:', newManagerUsers);

      setSelectedManager(newManagerUsers);
      setData({
        ...data,
        manager: userIds, // Store as [1, 2, 3] - plain IDs
        invitedManager: filteredEmails, // Store as ["email@test.com"] - filtered
      });
      clearFieldError(errors, setErrors, 'manager');

      // For level 1, do NOT add managers to global selected managers
    } else {
      // Single select for other levels
      const newManagerUser = e.value;

      console.log('Manager selected:', newManagerUser);

      const { isEmail, email, userId } = classifyItem(newManagerUser, users);

      // Filter out email if already used in other roles
      const isEmailAllowed =
        isEmail && getEmailExistingRoles(email, data, 'manager').length === 0;
      const filteredEmail = isEmailAllowed ? email : null;

      // Check and set error for blocked email
      if (isEmail) {
        checkAndSetEmailError(
          [email],
          data,
          'manager',
          errors,
          setErrors,
          'manager'
        );
      }

      setSelectedManager(newManagerUser);
      setData({
        ...data,
        manager: userId || null, // Store plain ID, treeUtils will wrap it
        invitedManager: filteredEmail,
      });
      clearFieldError(errors, setErrors, 'manager');

      // Handle global manager tracking
      if (userId) {
        const newManagerEntry = {
          node_id: node.id,
          manager: userId, // Store plain ID for global tracking
        };
        setGlobalSelectedManagers((prev) => {
          const filtered = prev.filter((item) => item.node_id !== node.id);
          return [...filtered, newManagerEntry];
        });
      } else {
        setGlobalSelectedManagers((prev) =>
          prev.filter((item) => item.node_id !== node.id)
        );
      }
    }
  };
};

/**
 * Creates event handler for employee changes (multi-select for all levels)
 * @param {Function} setSelectedEmployees - State setter for local UI display
 * @param {Function} setData - State setter for form data
 * @param {Object} data - Current form data: { name: 'Engineering', description: 'Tech team', manager: [1,2] or 3, head: [4,5] or 6, employees: [7,8,9] }
 * @param {Function} setGlobalSelectedEmployees - State setter for global employee tracking
 * @param {Object} node - Current node: { id: 'node1', level: 1, name: 'Engineering', manager: [1,2], head: [3,4], employees: [5,6,7] }
 * @returns {Function} - Event handler function
 *
 * Logic: When employees are selected:
 * 1. Updates local selectedEmployees state for UI
 * 2. Updates form data with formatted employee data (existing users or new invitations)
 * 3. Updates global state to prevent conflicts across nodes
 *
 * Example: User selects [emp1, "newemp@example.com", emp3] → formats as [{ type: 'existing', user_id: 7 }, { type: 'new', email: 'newemp@example.com' }, { type: 'existing', user_id: 9 }]
 */
export const createEmployeeChangeHandler = (
  setSelectedEmployees,
  setData,
  data,
  setGlobalSelectedEmployees,
  node,
  setErrors,
  errors,
  users
) => {
  return (e) => {
    const newEmployeeUsers = e.value || [];
    // Separate user IDs from emails (checks if email belongs to existing user)
    const { userIds, emails } = separateUsersAndEmails(newEmployeeUsers, users);

    // Filter out emails already used in other roles
    const filteredEmails = emails.filter(
      (email) => getEmailExistingRoles(email, data, 'employee').length === 0
    );

    // Check and set error for blocked emails
    checkAndSetEmailError(
      emails,
      data,
      'employee',
      errors,
      setErrors,
      'employees'
    );

    console.log('Employee data:', newEmployeeUsers);

    setSelectedEmployees(newEmployeeUsers);
    setData({
      ...data,
      employees: userIds, // Store as [1, 2, 3] - plain IDs
      invitedEmployees: filteredEmails, // Store as ["email@test.com"] - filtered
    });

    const newEmployeeEntry = {
      node_id: node.id,
      employees: userIds,
    };
    setGlobalSelectedEmployees((prev) => {
      const filtered = prev.filter((item) => item.node_id !== node.id);
      return [...filtered, newEmployeeEntry];
    });
  };
};
